import { useState } from 'react';

interface BubblePosition {
  x: number;
  y: number;
}

// Initialize from localStorage immediately to prevent position jump
const getInitialMinimized = (storageKey: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem(storageKey);
    return stored !== null ? stored === 'true' : false;
  } catch (e) {
    return false;
  }
};

const getInitialPosition = (storageKey: string): BubblePosition => {
  if (typeof window === 'undefined') {
    return { x: 16, y: 300 };
  }
  
  // CRITICAL: Clear any saved position FIRST
  localStorage.removeItem(storageKey);
  
  // ALWAYS start in BOTTOM RIGHT corner (accounting for header)
  const bubbleWidth = 400;
  const bubbleHeight = 600;
  const margin = 16;
  const headerHeight = 100; // Space for header
  
  const bottomRightX = window.innerWidth - bubbleWidth - margin;
  const bottomRightY = window.innerHeight - bubbleHeight - margin;
  
  const bottomRightPos = { 
    x: Math.max(margin, bottomRightX), 
    y: Math.max(headerHeight, bottomRightY) // Ensure below header
  };
  
  console.log('✅ MockDetector starting in BOTTOM RIGHT corner (below header):', { 
    bottomRightPos, 
    viewport: { w: window.innerWidth, h: window.innerHeight }
  });
  
  return bottomRightPos;
};

export const useMockDetectorSettings = (storagePrefix: string = '') => {
  const STORAGE_KEY_MINIMIZED = `${storagePrefix}mockdetector-bubble-minimized`;
  const STORAGE_KEY_POSITION = `${storagePrefix}mockdetector-bubble-position`;
  
  // Force clear position on hook initialization
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY_POSITION);
  }
  
  const [isMinimized, setIsMinimized] = useState<boolean>(() => getInitialMinimized(STORAGE_KEY_MINIMIZED));
  const [position, setPosition] = useState<BubblePosition>(() => getInitialPosition(STORAGE_KEY_POSITION));

  const toggleMinimize = () => {
    const newValue = !isMinimized;
    setIsMinimized(newValue);
    localStorage.setItem(`${storagePrefix}mockdetector-bubble-minimized`, String(newValue));
  };

  const minimize = () => {
    setIsMinimized(true);
    localStorage.setItem(`${storagePrefix}mockdetector-bubble-minimized`, 'true');
  };

  const maximize = () => {
    setIsMinimized(false);
    localStorage.setItem(`${storagePrefix}mockdetector-bubble-minimized`, 'false');
  };

  const updatePosition = (newPosition: BubblePosition) => {
    setPosition(newPosition);
    // REMOVED: Don't save to localStorage anymore - always start centered on refresh
    // localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(newPosition));
  };

  return {
    isMinimized,
    toggleMinimize,
    minimize,
    maximize,
    position,
    updatePosition
  };
};
